import { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { Add as AddIcon, AutoFixHigh, Payments } from '@mui/icons-material';
import {
  ActionIconButton,
  CustomButton,
  GeneralTableBox,
  HeadTableCell,
  HorizontalForm,
  NoDataSkeleton,
  PaginationTable,
  RowTableCell,
  StatusChip,
  TableBodySkeleton,
} from '../../../common/components';
import {
  useSupplierInvoiceAiStatus,
  useSupplierInvoiceAttachmentSignedUrl,
  useSupplierInvoices,
} from '../../../hooks/useSupplierLedger';
import { INVOICE_DISPLAY_STATUS_LABELS, compactFormSx, statusColor, tabPanelSx } from '../constants/supplierLedgerUiConstants';
import {
  getGeneratedPaymentOrderTooltip,
  getInvoiceDisplayStatus,
  hasGeneratedPaymentOrder,
  isInvoiceSelectableForPaymentOrder,
} from '../utils/supplierLedgerHelpers';
import { formatAmount } from '../utils/supplierLedgerFormatters';
import useClientPagination from '../hooks/useClientPagination';
import { ClassificationChips } from './SupplierLedgerShared';
import CancelInvoiceDialog from './CancelInvoiceDialog';
import CreatePaymentOrderDialog from './CreatePaymentOrderDialog';
import SupplierInvoiceDialog from './SupplierInvoiceDialog';

const InvoicesTab = ({ providers, providerMap }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  const [selected, setSelected] = useState({});
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);
  const { data: invoices = [], isLoading } = useSupplierInvoices();
  const { data: aiStatus } = useSupplierInvoiceAiStatus();
  const attachmentSignedUrl = useSupplierInvoiceAttachmentSignedUrl();

  const payableInvoices = useMemo(
    () => invoices.filter(isInvoiceSelectableForPaymentOrder),
    [invoices],
  );
  const payableInvoiceIds = useMemo(
    () => new Set(payableInvoices.map((invoice) => invoice.uuid)),
    [payableInvoices],
  );
  const selectedInvoices = payableInvoices.filter((invoice) => selected[invoice.uuid]);
  const canCreateOrder = selectedInvoices.length > 0;
  const invoicePagination = useClientPagination(invoices);
  const handleCloseInvoiceDialog = () => {
    setDialogOpen(false);
    setInvoiceToEdit(null);
  };

  const handleOpenAttachment = async (invoice) => {
    const attachment = invoice?.attachments?.[0];
    if (!attachment) return;
    try {
      const response = await attachmentSignedUrl.mutateAsync({
        invoiceId: invoice.uuid,
        attachmentId: attachment.uuid,
      });
      if (response?.signed_url) {
        window.open(response.signed_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Error al abrir adjunto:', err);
    }
  };

  useEffect(() => {
    setSelected((prev) => {
      let changed = false;
      const next = {};

      Object.entries(prev).forEach(([uuid, isSelected]) => {
        if (isSelected && payableInvoiceIds.has(uuid)) {
          next[uuid] = true;
          return;
        }
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [payableInvoiceIds]);

  return (
    <Box sx={tabPanelSx}>
      <HorizontalForm withBorder={false} sx={compactFormSx}>
        <CustomButton
          variant="base"
          startIcon={<AddIcon />}
          onClick={() => {
            setInvoiceToEdit(null);
            setDialogOpen(true);
          }}
        >
          Cargar factura
        </CustomButton>
        <CustomButton variant="outlined" startIcon={<AutoFixHigh />} disabled>
          {aiStatus?.message || 'Lectura IA próximamente'}
        </CustomButton>
        <CustomButton variant="outlined" startIcon={<Payments />} disabled={!canCreateOrder} onClick={() => setOrderDialogOpen(true)}>
          Generar OP
        </CustomButton>
      </HorizontalForm>
      <GeneralTableBox>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <HeadTableCell padding="checkbox" />
              <HeadTableCell>Fecha</HeadTableCell>
              <HeadTableCell>Proveedor</HeadTableCell>
              <HeadTableCell>Comprobante</HeadTableCell>
              <HeadTableCell>Concepto</HeadTableCell>
              <HeadTableCell>Clasificación</HeadTableCell>
              <HeadTableCell>Adjuntos</HeadTableCell>
              <HeadTableCell>Moneda</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Total ARS</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Pendiente ARS</HeadTableCell>
              <HeadTableCell>Estado</HeadTableCell>
              <HeadTableCell>Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodySkeleton />
            ) : invoices.length ? invoicePagination.paginatedRows.map((invoice) => {
              const hasGeneratedOrder = hasGeneratedPaymentOrder(invoice);
              const isSelectable = isInvoiceSelectableForPaymentOrder(invoice);
              const canCancel = invoice.status === 'open' && Number(invoice.paid_amount || 0) === 0;
              const canEdit = ['open', 'partially_paid'].includes(invoice.status);
              const displayStatus = getInvoiceDisplayStatus(invoice);
              return (
                <TableRow key={invoice.uuid} hover>
                  <RowTableCell padding="checkbox">
                    <Checkbox
                      checked={isSelectable && Boolean(selected[invoice.uuid])}
                      disabled={!isSelectable}
                      onChange={(event) => {
                        const { checked } = event.target;
                        setSelected((prev) => {
                          const next = { ...prev };
                          if (checked) {
                            next[invoice.uuid] = true;
                          } else {
                            delete next[invoice.uuid];
                          }
                          return next;
                        });
                      }}
                    />
                  </RowTableCell>
                  <RowTableCell>{invoice.invoice_date}</RowTableCell>
                  <RowTableCell>{providerMap[invoice.uuid_legal_entity] || invoice.provider_name || '-'}</RowTableCell>
                  <RowTableCell>{invoice.receipt_type} {invoice.receipt_number}</RowTableCell>
                  <RowTableCell>{invoice.concept || '-'}</RowTableCell>
                  <RowTableCell><ClassificationChips row={invoice} /></RowTableCell>
                  <RowTableCell>
                    {invoice.attachments?.length ? (
                      <Tooltip title={`Ver ${invoice.attachments[0].original_filename || 'adjunto'}`}>
                        <span>
                          <ActionIconButton
                            variant="download"
                            onClick={() => handleOpenAttachment(invoice)}
                            disabled={attachmentSignedUrl.isPending}
                          />
                        </span>
                      </Tooltip>
                    ) : '-'}
                  </RowTableCell>
                  <RowTableCell>{invoice.currency || 'ARS'}</RowTableCell>
                  <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(invoice.total_amount_ars)}</RowTableCell>
                  <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(invoice.pending_amount_ars)}</RowTableCell>
                  <RowTableCell>
                    <StatusChip
                      size="small"
                      color={statusColor[displayStatus] || 'default'}
                      label={INVOICE_DISPLAY_STATUS_LABELS[displayStatus] || displayStatus}
                      tooltip={hasGeneratedOrder ? getGeneratedPaymentOrderTooltip(invoice) : undefined}
                    />
                  </RowTableCell>
                  <RowTableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Tooltip title={canEdit ? (hasGeneratedOrder ? 'Editar datos descriptivos y clasificación' : 'Editar factura') : 'Sólo se pueden editar facturas pendientes'}>
                        <span>
                          <ActionIconButton
                            variant="edit"
                            disabled={!canEdit}
                            onClick={() => {
                              setInvoiceToEdit(invoice);
                              setDialogOpen(true);
                            }}
                          />
                        </span>
                      </Tooltip>
                      <Tooltip title={canCancel ? (hasGeneratedOrder ? 'Anular factura y OP asociada' : 'Anular factura') : 'Sólo se pueden anular facturas abiertas sin pagos'}>
                        <span>
                          <ActionIconButton
                            variant="delete"
                            disabled={!canCancel}
                            onClick={() => setInvoiceToCancel(invoice)}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  </RowTableCell>
                </TableRow>
              );
            }) : (
              <NoDataSkeleton colSpan={12} message="No hay facturas de proveedores cargadas." />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
      {!!invoices.length && (
        <PaginationTable
          page={invoicePagination.page}
          totalPages={invoicePagination.totalPages}
          totalCount={invoicePagination.totalCount}
          rowsPerPage={invoicePagination.rowsPerPage}
          onPageChange={invoicePagination.handlePageChange}
          onRowsPerPageChange={invoicePagination.handleRowsPerPageChange}
        />
      )}
      <SupplierInvoiceDialog
        open={dialogOpen}
        onClose={handleCloseInvoiceDialog}
        providers={providers}
        invoiceToEdit={invoiceToEdit}
      />
      <CreatePaymentOrderDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        invoices={selectedInvoices}
        providerMap={providerMap}
      />
      <CancelInvoiceDialog
        open={Boolean(invoiceToCancel)}
        invoice={invoiceToCancel}
        providerName={invoiceToCancel ? providerMap[invoiceToCancel.uuid_legal_entity] : ''}
        onClose={() => setInvoiceToCancel(null)}
      />
    </Box>
  );
};

export default InvoicesTab;
